"use client";
import React, { ReactNode, useEffect, useState } from "react";

import { BriefcaseIcon, SparklesIcon } from "@heroicons/react/24/solid";

import { classNames } from "@lib/helpers";

import Job from "./_components/JobCard";
import useJobLoadingStore from "@store/isLoadingJobs";
import JobsListingLoader from "./Loading/JobsListingLoader";

function MainContent({
  recommendedJobs,
  allJobs,
  totalJobs,
  isFilterOpen,
  searchParams,
}: any) {
  const [activeTab, setActiveTab] = useState<"recommendedJobs" | "allJobs">();
  const { isLoading, setIsLoading }: any = useJobLoadingStore();

  useEffect(() => {
    if (!recommendedJobs?.length && allJobs?.length) {
      setActiveTab("allJobs");
    } else if (!allJobs?.length && recommendedJobs?.length) {
      setActiveTab("recommendedJobs");
    } else if (recommendedJobs?.length && allJobs?.length) {
      setActiveTab("recommendedJobs");
    }
    setIsLoading(false);
  }, [recommendedJobs, allJobs]);

  const jobsToDisplay =
    activeTab === "recommendedJobs" && !isFilterOpen
      ? recommendedJobs
      : allJobs;

  if (isLoading) return <JobsListingLoader search={searchParams?.search} />;

  return (
    <>
      {searchParams?.search ? (
        <header className="h-14 lg:h-[60px] rounded-t-[12px] border-[#E7E7E7] px-5 md:px-8 lg:px-10 border mb-5 flex items-center">
          <h2 className="text-base sm:text-lg text-[#1F2223]">
            <strong className="font-semibold text-[#F59A00]">
              {totalJobs}
            </strong>{" "}
            Jobs found
          </h2>
        </header>
      ) : null}
      <div className="w-full xl:w-auto flex flex-col gap-5">
        {!isFilterOpen ? (
          <div className="relative">
            <div className="flex flex-row items-center justify-start -mb-0.5 z-20 relative">
              {recommendedJobs?.length > 0 && (
                <Tab
                  name="recommendedJobs"
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  icon={<SparklesIcon className="w-5 h-5" />}
                >
                  Recommended <span className="hidden md:inline">for you</span>
                </Tab>
              )}

              {allJobs?.length > 0 && (
                <Tab
                  name="allJobs"
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  icon={<BriefcaseIcon className="w-5 h-5" />}
                >
                  All jobs
                </Tab>
              )}
            </div>
            <div className="relative z-10 h-0.5 w-full bg-[#F0F2F5]"></div>
          </div>
        ) : null}

        <div className="flex flex-col last:!border-0 gap-5">
          <>
            {jobsToDisplay.length > 0 ? (
              jobsToDisplay.map((job: any, i: number) => (
                <Job job={job} key={job.id || i} />
              ))
            ) : (
              <div className="border py-3 border-[#E7E7E7] text-[#1F2223] px-5 md:px-8 lg:px-10 text-center">
                <p>No jobs found.</p>
              </div>
            )}
          </>
        </div>
      </div>
    </>
  );
}

export default MainContent;

interface TabProps {
  name: "recommendedJobs" | "allJobs";
  children: ReactNode;
  icon: ReactNode;
  activeTab: "recommendedJobs" | "allJobs" | undefined;
  setActiveTab: (
    value: React.SetStateAction<"recommendedJobs" | "allJobs" | undefined>
  ) => void;
}
const Tab = ({ icon, name, children, activeTab, setActiveTab }: TabProps) => {
  const isActive = activeTab === name;
  return (
    <button
      onClick={() => setActiveTab(name)}
      className={classNames(
        `h-12 md:h-[56px] font-medium flex flex-row items-center justify-center transition-colors gap-[8px] px-6 pt-0 pb-0 xl:text-[16px] lg:text-[16px] md:text-[16px] text-[13px] border-b-2`,
        isActive
          ? "text-secondary border-secondary"
          : "text-neutral-700 border-transparent hover:bg-neutral-100 hover:border-[#F0F2F5]"
      )}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
};
